import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="9124" className="flex gap-8 animate-pulse">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-20 bg-[#DFE2E5] rounded" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-sm font-medium tracking-tight text-[#637381] hover:text-[#66CCFF] transition-colors font-sans">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) return null;
  return <nav className="flex flex-col items-center gap-8 mt-12">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-2xl font-bold tracking-tight text-[#212B36] hover:text-[#66CCFF] transition-colors font-sans" onClick={onClose}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function ThePulseHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className="bg-white text-[#212B36] w-full relative z-50">
      <header className={`sticky top-0 left-0 right-0 w-full transition-all duration-300 z-50 h-16 flex items-center ${isScrolled ? "bg-white/80 backdrop-blur-lg shadow-sm" : "bg-white border-b border-[#DFE2E5]"}`}>
        <div className="container mx-auto px-4 flex items-center justify-between">
          {}
          <div className="flex-shrink-0">
            <Link to="/HomePage" className="block">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1600388/img/dp_monogram_logo.png" alt="Crash Report Finder Logo" className="h-10 w-auto object-contain" />
            </Link>
          </div>

          {}
          <div className="hidden md:block">
            <MenuProvider menu_id="16" className="flex items-center">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="flex items-center gap-4">
            {}
            <Button asChild variant="ghost" className="relative overflow-hidden p-0 h-auto bg-transparent hover:bg-transparent text-sm font-medium tracking-tight text-[#637381] hover:text-[#66CCFF] transition-all duration-300 group hidden sm:inline-flex">
              <Link to="#">
                <span className="relative z-10">Support</span>
                {}
                <div className="absolute inset-0 rounded-md border border-[#66CCFF] opacity-0 pointer-events-none" />
              </Link>
            </Button>

            {}
            <Button variant="ghost" size="icon" className="md:hidden text-[#212B36] hover:text-[#66CCFF] hover:bg-transparent" onClick={() => setIsMobileMenuOpen(true)}>
              <Menu className="h-6 w-6" />
              <span className="sr-only">Open menu</span>
            </Button>
          </div>
        </div>
      </header>

      {}
      {isMobileMenuOpen && <div className="fixed inset-0 z-[60] bg-white/95 backdrop-blur-xl flex flex-col animate-in fade-in duration-200">
          <div className="container mx-auto px-4 h-20 flex items-center justify-between border-b border-[#DFE2E5]">
            <Link to="/HomePage" onClick={() => setIsMobileMenuOpen(false)}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1600388/img/dp_monogram_logo.png" alt="Crash Report Finder Logo" className="h-8 w-auto object-contain" />
            </Link>
            <Button variant="ghost" size="icon" className="text-[#212B36] hover:text-[#66CCFF] hover:bg-transparent" onClick={() => setIsMobileMenuOpen(false)}>
              <X className="h-6 w-6" />
              <span className="sr-only">Close menu</span>
            </Button>
          </div>
          
          <div className="flex-1 flex flex-col items-center justify-center p-4">
            <MenuProvider menu_id="16" className="w-full flex justify-center">
              <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>

            <div className="mt-12">
              <Button asChild size="lg" className="relative overflow-hidden bg-[#66CCFF] text-white hover:bg-[#66CCFF]/90 w-full min-w-[200px] rounded-lg shadow-sm">
                <Link to="#" onClick={() => setIsMobileMenuOpen(false)}>
                  Support
                </Link>
              </Button>
            </div>
          </div>
        </div>}
    </section>;
}